import { FC, useRef } from "react"
import { Upload } from "lucide-react"
import {
  ConnectorFormValues,
  ConnectorFieldMapping,
  ConnectorFieldMappingEntry
} from "../../../../../../types"

// Normalize legacy object-format field mapping to array format
const normalizeImportedFieldMapping = (
  mapping: any,
  direction?: string
): ConnectorFieldMapping => {
  if (Array.isArray(mapping)) return mapping
  if (mapping && typeof mapping === "object") {
    const isIncoming = direction !== "outgoing"
    return Object.entries(mapping).map(([key, value]: [string, any]) => {
      const entry: ConnectorFieldMappingEntry = { key: "" }
      if (typeof value === "string") {
        if (isIncoming) {
          entry.incoming_key = key
          entry.key = value
        } else {
          entry.key = key
          entry.outgoing_key = value
        }
      } else if (value && typeof value === "object" && "key" in value) {
        if (isIncoming) {
          // "crmField": { key: "bbField", values?: {...} }
          entry.incoming_key = key
          entry.key = value.key
        } else {
          // "bbField": { key: "crmField", values?: {...} }
          entry.key = key
          entry.outgoing_key = value.key
        }
        if (value.values) {
          entry.values = value.values
        }
      }
      return entry
    })
  }
  return []
}

interface Props {
  onImport: (data: Partial<ConnectorFormValues>) => void
}

const ImportConnector: FC<Props> = ({ onImport }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = e => {
      try {
        const content = e.target?.result as string
        const jsonData = JSON.parse(content)

        // Validate and extract connector data
        const importedData: Partial<ConnectorFormValues> = {
          name: jsonData.name || "",
          type: jsonData.type || "webhook",
          brand_id: jsonData.brand_id || "",
          owner: jsonData.owner || "",
          direction: jsonData.direction || "outgoing",
          data_type: jsonData.data_type || "lead",
          webhook_url: jsonData.webhook_url || "",
          event: jsonData.event || "",
          field_mapping: normalizeImportedFieldMapping(
            jsonData.field_mapping,
            jsonData.direction
          ),
          template: jsonData.template || "",
          config: jsonData.config || {},
          active:
            typeof jsonData.active === "string"
              ? parseInt(jsonData.active)
              : jsonData.active || 1
        }

        // Parse config if it's null/string
        if (!importedData.config || importedData.config === null) {
          importedData.config = {}
        }

        // Log imported data for debugging
        console.log("Importing connector data:", importedData)

        onImport(importedData)

        // Show success message
        alert(
          `Successfully imported connector configuration: ${
            importedData.name || "Unnamed"
          }`
        )

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      } catch (error) {
        console.error("Failed to parse JSON file:", error)
        alert("Invalid JSON file. Please check the file format and try again.")
      }
    }

    reader.readAsText(file)
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileSelect}
        className="hidden"
      />
      <button
        type="button"
        onClick={handleButtonClick}
        className="px-3 py-1 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
      >
        <Upload size={14} className="mr-1" />
        Import JSON
      </button>
    </>
  )
}

export default ImportConnector
